import React, { useState, useEffect, useRef } from "react";
import { Navigate } from "react-router-dom";
import Grid from "@mui/material/Grid2";

import { 
  Campaign, 
  Character,
} from "@dndModels";
import API from "@dndUtilities/system_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";

import DandDButtonBox from "@dndComponents/DandDButtonBoxSimple";
import CharConnectUtilities from "@dndUtilities/char_connect_utilities";
import CampaignCharacterInput from "./CampaignCharacterInput";

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const connect_util = CharConnectUtilities.getInstance();

interface Props {
  campaign: Campaign;
}


export default function CampaignCharactersInput({ campaign }: Props) {
  const [reloading, setReloading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const [my_characters, setMyCharacters] = useState<Character[]>([]);
  const [add_existing, setAddExisting] = useState(false);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);
    
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  if (navigateTo !== null) {
    return <Navigate to={navigateTo} />;
  } else if (add_existing) {
    return (
      <Grid container spacing={0} direction="column">
        <Grid>
          Adding an existing character which doesn"t comply with the allowed options for this campaign will automatically undo those options on the character.<br/>
          This may include race, subrace, class, subclass, gifts, items, and spells.
        </Grid>
        <Grid container spacing={0} direction="row">
          { my_characters.map(char => {
            return (
              <Grid size={4} key={char._id}
                container spacing={0} direction="row">
                <Grid size={12}>{ char.name }</Grid>
                { char.old_campaign_id === campaign._id ? 
                  <Grid size={12}>Already in Campaign</Grid>
                : char.old_campaign_id === "" ? 
                  <Grid size={12}>
                    <DandDButtonBox name="Join (can take a while)" 
                      disabled={ processing || !api.online } 
                      onClick={ async () => {
                        setProcessing(true);
                        char.old_campaign_id = campaign._id;
                        connect_util.connectCampaign2(char, campaign);
                        const res = await char_util.getCharacter({id: char._id});
                        if (res) {
                          const new_char = res;
                          new_char.old_campaign_id = new_char._id;
                          connect_util.connectCampaign2(new_char, campaign);
                          new_char.last_campaign_update = 0;
                          await api.updateCharacter(new_char, ["main"]);
                          await api.updateCharacter(new_char, ["main"], true);
                          campaign.characters.push(new_char);
                          setAddExisting(false);
                          setProcessing(false);
                        }
                      }}
                    />
                  </Grid>
                : 
                  <Grid size={12}>In a Different Campaign</Grid>
                }
              </Grid>
            );
          })}
        </Grid>
        <Grid>
          <DandDButtonBox name="Cancel" 
            disabled={ processing }
            onClick={() => {
              setAddExisting(false);
            }}
          />
        </Grid>
      </Grid>
    );
  } else {
    return (
      <Grid container spacing={0} direction="row">
        { api.user && 
          <Grid size={12}
            container spacing={0} direction="row">
            <Grid size={4}>
              <DandDButtonBox name="Add Existing Character" 
                disabled={ processing || !api.online } 
                onClick={ async () => {
                  if (my_characters.length > 0) {
                    setAddExisting(true);
                  } else if (api.user) {
                    setProcessing(true);
                    setAddExisting(true);
                    const filter: any = {};
                  
                    filter.owner_id = api.user._id;
                    const res = await api.getObjects<Character>({ data_type: "character", filter });
                    setMyCharacters(res);
                    setProcessing(false);
                  }
                }}
              />
            </Grid>
            <Grid size={2}>
              <DandDButtonBox name="Create Character" 
                disabled={ processing || !api.online } 
                onClick={() => {
                  if (api.user) {
                    setProcessing(true);
                    const new_char = new Character();
                    new_char.owner_id = api.user._id;
                    new_char.old_campaign_id = new_char._id;
                    connect_util.connectCampaign2(new_char, campaign);
                    new_char.last_campaign_update = campaign.last_updated;
                    char_util.enforceOptions(new_char, campaign).then(res => {
                      api.createObject("character", new_char).then(res => {
                        if (res && res.body.id) {
                          setProcessing(false);
                          setNavigateTo(`/character/${res.body.id}`);
                        } else {
                          console.log(res);
                        }
                      });
                    });
                  }
                }}
              />
            </Grid>
            <Grid size={2}>
              <DandDButtonBox name="Create Sidekick" 
                disabled={ processing || !api.online } 
                onClick={() => {
                  if (api.user) {
                    setProcessing(true);
                    const new_char = new Character();
                    new_char.owner_id = api.user._id;
                    new_char.old_campaign_id = new_char._id;
                    new_char.sidekick = true;
                    connect_util.connectCampaign2(new_char, campaign);
                    new_char.last_campaign_update = campaign.last_updated;
                    char_util.enforceOptions(new_char, campaign).then(res => {
                      api.createObject("character", new_char).then(res => {
                        if (res && res.body.id) {
                          setProcessing(false);
                          setNavigateTo(`/character/${res.body.id}`);
                        } else {
                          console.log(res);
                        }
                      });
                    });
                  }
                }}
              />
            </Grid>
            <Grid size={2}>
              <DandDButtonBox name="Create Unassigned Character" 
                disabled={ processing || !api.online } 
                onClick={() => {
                  setProcessing(true);
                  const new_char = new Character();
                  new_char.owner_id = "";
                  new_char.old_campaign_id = new_char._id;
                  connect_util.connectCampaign2(new_char, campaign);
                  new_char.last_campaign_update = campaign.last_updated;
                  char_util.enforceOptions(new_char, campaign).then(res => {
                    api.createObject("character", new_char).then(res => {
                      if (res && res.body.id) {
                        setProcessing(false);
                        setNavigateTo(`/character/${res.body.id}`);
                      } else {
                        console.log(res);
                      }
                    });
                  });
                }}
              />
            </Grid>
            <Grid size={2}>
              <DandDButtonBox name="Create Unassigned Sidekick" 
                disabled={ processing || !api.online } 
                onClick={() => {
                  setProcessing(true);
                  const new_char = new Character();
                  new_char.sidekick = true;
                  new_char.owner_id = "";
                  new_char.old_campaign_id = new_char._id;
                  connect_util.connectCampaign2(new_char, campaign);
                  new_char.last_campaign_update = campaign.last_updated;
                  char_util.enforceOptions(new_char, campaign).then(res => {
                    api.createObject("character", new_char).then(res => {
                      if (res && res.body.id) {
                        setProcessing(false);
                        setNavigateTo(`/character/${res.body.id}`);
                      } else {
                        console.log(res);
                      }
                    });
                  });
                }}
              />
            </Grid>
          </Grid>
        }
        { !reloading && campaign.c_chars.filter(o => o.active).length > 0 &&
          <>
            <Grid size={12} className="header centered">Active Characters</Grid>
            <Grid size={12}
              container spacing={0} direction="row">
              { campaign.c_chars.filter(o => o.active).map(c_char => {
                return (
                  <Grid size={4} key={c_char._id}>
                    <CampaignCharacterInput
                      c_char={c_char} 
                      campaign={campaign} 
                      // updateCampaign={() => { setReloading(true); }}
                      onUpdated={() => { setReloading(true); }}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </>
        }
        { !reloading && campaign.c_chars.filter(o => !o.active).length > 0 &&
          <>
            <Grid size={12} className="header centered">Inactive Characters</Grid>
            <Grid size={12}
              container spacing={0} direction="row">
              { campaign.c_chars.filter(o => !o.active).map(c_char => {
                return (
                  <Grid size={4} key={c_char._id}>
                    <CampaignCharacterInput
                      c_char={c_char} 
                      campaign={campaign} 
                      // updateCampaign={() => { setReloading(true); }}
                      onUpdated={() => { setReloading(true); }}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </>
        }
      </Grid>
    ); 
  }
}
